/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import FormGroup from "@mui/material/FormGroup";
import { FormControl } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextareaAutosize from "@mui/material/TextareaAutosize";

import GridSectionHeader from "../../../widgets/GridSectionHeader";
import GridCheckbox from "../../../widgets/GridCheckbox";
import { valudateTextfield2 } from "../../../../utils/fieldValidator.js";
import { MAX_LENGTH_TEXTAREA } from "../../../../utils/constants.js";

//DATA SLICE - REDUCER
import { updateCKACKR } from "../../../../states/slcCkaCkr.js";

var isEducationQualifiedError = "";
var eduFieldsError = "";
var educationInstituteError = "";

//TRUE - WHEN COMPONENT IS VALID
var validCKAEducation = false;

export function getCKAEducationValidity() {
  return validCKAEducation;
}

export default function SectionCKAEducation({ setEnableNext }) {
  const ckackr = useSelector((state) => state.ckackr);

  const [isEducationQualified, setIsEducationQualified] = useState(ckackr.isEducationQualified);
  const [eduFields, setEduFields] = useState(Array(11).fill(""));
  const [educationInstitute, setEducationInstitute] = useState(ckackr.educationInstitute);

  const dispatch = useDispatch();

  useEffect(() => {
    const data = {
      isEducationQualified: isEducationQualified,
      eduFields: eduFields,
      educationInstitute: educationInstitute,
    };
    updateRedux("education", data, updateCKACKR);
  }, [isEducationQualified, eduFields, educationInstitute]);

  function updateRedux(fieldName, fieldValue, reducer) {
    const action = {
      type: fieldName,
      payload: fieldValue,
    };
    dispatch(reducer(action));
  }

  function handleChange(e, id) {
    let input_data = {
      event: e,
      fieldName: "",
      fieldVariable: "",
      isMandatory: false,
    };

    input_data.e = e;

    let type = "";
    let name = "";

    id = e.target.id.toLowerCase();

    //RADIOBOX
    if (id === "") {
      type = e.target.type;
      name = e.target.name;
      validateRadioBox(e, type, name);
    }

    //CHECKBOX
    validateCheckbox(e, id);

    //TEXTFIELDS
    validateTextfield(id, input_data);

    calculateValidity();
  }

  function validateRadioBox(e, type, name) {
    if (type === "radio" && name === "educationQualified") {
      setIsEducationQualified(e.target.value);
      isEducationQualifiedError = "";
    }
  }

  function validateCheckbox(e, id) {
    const newArr = [...eduFields];
    const selectedEducationFIeld = e.target.value.toUpperCase();
    let index = 0;

    if (id !== "") {
      switch (selectedEducationFIeld) {
        case "ACCOUNTANCY":
          index = 0;
          break;

        case "ACTURIAL SCIENCE":
          index = 1;
          break;

        case "CAPITAL MARKET":
          index = 2;
          break;

        case "COMMERCE":
          index = 3;
          break;

        case "ECONOMICS":
          index = 4;
          break;

        case "FINANCIAL ENGINEERING":
          index = 5;
          break;

        case "FINANCIAL PLANNING":
          index = 6;
          break;

        case "FINANCE":
          index = 7;
          break;

        case "COMPUTATIONAL FINANCES":
          index = 8;
          break;

        case "INSURANCE":
          index = 9;
          break;

        case "BUSINESS":
          index = 10;
          break;

        default:
          index = -1;
      }

      newArr[index] = newArr[index] === "" ? selectedEducationFIeld : "";
      setEduFields(newArr);

      //Check & indicate an education is already selected
      let isSelected = false;
      for (const eduField of newArr) {
        if (eduField.length !== 0) {
          isSelected = true;
          break;
        }
      }
      if (isSelected) eduFieldsError = "";
      else eduFieldsError = "Qualification must be selected";
    }
  }

  function validateTextfield(id, input_data) {
    if (id === "educationinstitute") {
      input_data.fieldName = "Qualification description";
      input_data.fieldVariable = educationInstitute;
      input_data.fieldLength = MAX_LENGTH_TEXTAREA;
      input_data.isMandatory = true;
      const result = valudateTextfield2(input_data);

      if (result.errorMessage === "") {
        setEducationInstitute(result.value);
        educationInstituteError = "";
      } else educationInstituteError = "Qualification description is required";
    }
  }

  function calculateValidity() {
    validCKAEducation = false;

    if (isEducationQualifiedError.length !== 0 || educationInstituteError.length !== 0 || eduFieldsError.length !== 0) {
      validCKAEducation = false;
      setEnableNext(false);
      return;
    }

    validCKAEducation = true;
    setEnableNext(true);
  }

  return (
    <>
      <Grid container spacing={2}>
        <GridSectionHeader
          sectionName="Educational Qualifications"
          sx={{
            p: 1,
            pl: 1.8,
            mt: 0,
            mb: 1,
            fontSize: 18,
            fontWeight: "bold",
            color: "#FFFFFF", //WHITE
            // color: "#000000", //BLACK
            // backgroundColor: "#d9d9d9",
            backgroundColor: "#10B981",
          }}
        />

        <Typography variant="body1">
          1. Do you have a diploma or higher qualification in any of the following fields?
        </Typography>
      </Grid>

      <RadioGroup row name="educationQualified" defaultValue="" value={isEducationQualified} onChange={handleChange}>
        <FormControl>
          <Stack direction="row">
            <FormControlLabel id="isEducationQualifiedYes" control={<Radio />} label="Yes" value="Yes" />

            <FormControlLabel id="educationQualifiedNo" control={<Radio />} label="No" value="No" />
          </Stack>
          {isEducationQualifiedError && (
            <FormHelperText error={isEducationQualifiedError}>{isEducationQualifiedError}</FormHelperText>
          )}
        </FormControl>
      </RadioGroup>

      {/*==============================
          FIELDS OF EDUCATION
         ==============================*/}
      <Typography variant="h6" gutterBottom sx={{ p: 2, my: 1, border: "1px solid", borderRadius: 5 }}>
        <FormGroup>
          <Stack direction="row">
            <GridCheckbox
              id="eduAccountancy"
              columnSize="4"
              label="Accountancy"
              value="Accountancy"
              disabled={isEducationQualified === "Yes" ? false : true}
              onChange={handleChange}
            />
            <GridCheckbox
              id="eduActurialScience"
              columnSize="4"
              label="Acturial Science"
              value="Acturial Science"
              disabled={isEducationQualified === "Yes" ? false : true}
              onChange={handleChange}
            />
            <GridCheckbox
              id="eduCapitalMarket"
              columnSize="4"
              label="Capital Market"
              value="Capital Market"
              disabled={isEducationQualified === "Yes" ? false : true}
              onChange={handleChange}
            />
          </Stack>

          <Stack direction="row">
            <GridCheckbox
              id="eduCommerce"
              columnSize="4"
              label="Commerce"
              value="Commerce"
              disabled={isEducationQualified === "Yes" ? false : true}
              onChange={handleChange}
            />
            <GridCheckbox
              id="eduEconomics"
              columnSize="4"
              label="Economics"
              value="Economics"
              disabled={isEducationQualified === "Yes" ? false : true}
              onChange={handleChange}
            />
            <GridCheckbox
              id="eduFinancialEngineering"
              columnSize="4"
              label="Financial Engineering"
              value="Financial Engineering"
              disabled={isEducationQualified === "Yes" ? false : true}
              onChange={handleChange}
            />
          </Stack>

          <Stack direction="row">
            <GridCheckbox
              id="eduFinancialPlanning"
              columnSize="4"
              label="Financial Planning"
              value="Financial Planning"
              disabled={isEducationQualified === "Yes" ? false : true}
              onChange={handleChange}
            />
            <GridCheckbox
              id="eduFinance"
              columnSize="4"
              label="Finance"
              value="Finance"
              disabled={isEducationQualified === "Yes" ? false : true}
              onChange={handleChange}
            />
            <GridCheckbox
              id="eduComputationalFinance"
              columnSize="4"
              label="Computational Finances"
              value="Computational Finances"
              disabled={isEducationQualified === "Yes" ? false : true}
              onChange={handleChange}
            />
          </Stack>

          <Stack direction="row">
            <GridCheckbox
              id="eduInsurance"
              columnSize="4"
              label="Insurance"
              value="Insurance"
              disabled={isEducationQualified === "Yes" ? false : true}
              onChange={handleChange}
            />
            <GridCheckbox
              id="eduBusiness"
              columnSize="8"
              label="Business / Bus Admin / Bus Management/ Bus Studies"
              value="Business"
              disabled={isEducationQualified === "Yes" ? false : true}
              onChange={handleChange}
            />
          </Stack>
        </FormGroup>
        {eduFieldsError && <FormHelperText error={eduFieldsError}>{eduFieldsError}</FormHelperText>}
      </Typography>

      {/*==============================
        EDUCATION INSTITUTE DETAILS
      ==============================*/}
      {isEducationQualified === "Yes" && (
        <Grid size={12}>
          <Typography variant="body1"></Typography>

          <TextareaAutosize
            id="educationInstitute"
            placeholder="Type here..."
            style={{ width: "100%" }}
            minRows={5}
            onChange={handleChange}
            defaultValue={educationInstitute}
          />
          {educationInstituteError && (
            <FormHelperText error={educationInstituteError}>{educationInstituteError}</FormHelperText>
          )}
        </Grid>
      )}
    </>
  );
}
